import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FeedbackAttachment, FeedbackItem, FeedbackListBody, ParentFeedbackService } from './parent-feedback.service';

const PAGE_LIMIT = 10;

// Attachment limits — single source of truth (see decision #3 in the
// feature brief: the mobile app's own gap was duplicating these numbers
// between the pick-time validator and the UI hint text; this constant is
// referenced from both instead).
export const FEEDBACK_MAX_COUNT = 3;
export const FEEDBACK_MAX_TOTAL_SIZE_BYTES = 10 * 1024 * 1024;

declare var customToastr: any;
// customToastr is a global loaded by the surrounding shell (same helper
// parent-activity/parent-chat already declare) — guarded the same way so a
// missing/not-yet-loaded global never throws.
function safeToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  if (typeof customToastr === 'function') {
    customToastr(message, type);
  }
}

interface AttachmentPreview {
  file: File;
  isImage: boolean;
  objectUrl: string | null;
}

// Feedback — a free-text complaint/suggestion box with optional media
// attachments and an optional school reply. Authenticated-only per the
// feature's scope decision: this component never calls the public/pre-login
// submit path (api/v3/public/feedback) — it only uses the authenticated
// list + submit APIs, exactly like every other parent-portal feature here.
// There's no rating/category/status concept anywhere in this feature.
@Component({
  selector: 'app-parent-feedback',
  templateUrl: './parent-feedback.component.html',
  styleUrls: ['./parent-feedback.component.css'],
})
export class ParentFeedbackComponent implements OnInit, OnChanges {

  // The API resolves the parent/student from the auth token (no
  // studentId/classId is ever sent per the spec), so this @Input() exists
  // purely so ParentLoginComponent's studentSwitchTick (bumped on every
  // switchStudent()) can trigger ngOnChanges and force a reload — the same
  // reload-on-switch convention parent-notifications/parent-my-teacher
  // follow despite having no real per-student query params either.
  @Input() studentSwitchTick = 0;

  items: FeedbackItem[] = [];
  currentPage = 1;
  maxPageNumber = 1;
  loading = false;
  error = '';

  expandedIds = new Set<number>();

  private requestId = 0;

  // ---------- Submit modal state ----------
  showSubmitModal = false;
  message = '';
  attachments: AttachmentPreview[] = [];
  submitting = false;
  submitError = '';

  // ---------- Attachment viewers ----------
  imageViewerUrl: string | null = null;
  videoViewerUrl: string | null = null;

  readonly maxAttachmentCount = FEEDBACK_MAX_COUNT;
  readonly maxTotalSizeBytes = FEEDBACK_MAX_TOTAL_SIZE_BYTES;

  constructor(private feedbackService: ParentFeedbackService) { }

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentSwitchTick'] && !changes['studentSwitchTick'].firstChange) {
      this.currentPage = 1;
      this.expandedIds.clear();
      this.loadList();
    }
  }

  loadList(): void {
    this.loading = true;
    this.error = '';
    const requestId = ++this.requestId;
    this.feedbackService.getFeedbackList(this.currentPage, PAGE_LIMIT).subscribe({
      next: (res: any) => {
        if (requestId !== this.requestId) { return; }
        this.loading = false;
        if (res && res.status && res.body) {
          const body: FeedbackListBody = res.body;
          this.items = body.feedbackList || [];
          this.maxPageNumber = body.maxPageNumber || 1;
        } else {
          this.items = [];
          this.maxPageNumber = 1;
          const msg = (res && res.message) || 'Unable to load feedback.';
          this.error = msg;
          safeToast(msg, 'error');
        }
      },
      error: (err) => {
        if (requestId !== this.requestId) { return; }
        this.loading = false;
        this.items = [];
        const msg = (err && err.error && err.error.message) || 'Unable to load feedback.';
        this.error = msg;
        safeToast(msg, 'error');
      },
    });
  }

  refresh(): void {
    this.loadList();
  }

  goToPrevPage(): void {
    if (this.currentPage <= 1) { return; }
    this.currentPage--;
    this.loadList();
  }

  goToNextPage(): void {
    if (this.currentPage >= this.maxPageNumber) { return; }
    this.currentPage++;
    this.loadList();
  }

  trackByFeedbackId(_: number, item: FeedbackItem): number {
    return item.feedbackId;
  }

  isExpanded(item: FeedbackItem): boolean {
    return this.expandedIds.has(item.feedbackId);
  }

  toggleExpand(item: FeedbackItem): void {
    if (this.expandedIds.has(item.feedbackId)) {
      this.expandedIds.delete(item.feedbackId);
    } else {
      this.expandedIds.add(item.feedbackId);
    }
  }

  isImageAttachment(att: FeedbackAttachment): boolean {
    if (att?.urlType) { return att.urlType.toLowerCase() === 'image'; }
    return /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(att?.fileUrl || '');
  }

  openAttachment(att: FeedbackAttachment): void {
    if (!att?.fileUrl) { return; }
    if (this.isImageAttachment(att)) {
      this.imageViewerUrl = att.fileUrl;
    } else {
      this.videoViewerUrl = att.fileUrl;
    }
  }

  closeImageViewer(): void {
    this.imageViewerUrl = null;
  }

  closeVideoViewer(): void {
    this.videoViewerUrl = null;
  }

  // ---------- Submit modal ----------

  openSubmitModal(): void {
    this.showSubmitModal = true;
    this.submitError = '';
    this.message = '';
    this.revokeAttachmentUrls();
    this.attachments = [];
  }

  closeSubmitModal(): void {
    if (this.submitting) { return; }
    this.showSubmitModal = false;
    this.revokeAttachmentUrls();
    this.attachments = [];
  }

  private revokeAttachmentUrls(): void {
    for (const a of this.attachments) {
      if (a.objectUrl) { URL.revokeObjectURL(a.objectUrl); }
    }
  }

  private isAllowedFile(file: File): boolean {
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) { return true; }
    return /\.(png|jpe?g|mp4|mov|avi|quicktime|x-msvideo)$/i.test(file.name);
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const picked = Array.from(input.files || []);
    input.value = '';
    this.addAttachments(picked);
  }

  isDraggingOverAttach = false;

  onAttachDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingOverAttach = true;
  }

  onAttachDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingOverAttach = false;
  }

  onAttachmentDropped(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingOverAttach = false;
    const dropped = Array.from(event.dataTransfer?.files || []);
    this.addAttachments(dropped);
  }

  private addAttachments(picked: File[]): void {
    if (!picked.length) { return; }

    if (this.attachments.length + picked.length > this.maxAttachmentCount) {
      safeToast(`You can attach up to ${this.maxAttachmentCount} files.`, 'error');
      return;
    }

    const invalid = picked.find(f => !this.isAllowedFile(f));
    if (invalid) {
      safeToast(`${invalid.name} is not a supported image or video file.`, 'error');
      return;
    }

    // Validation happens at pick time, not at submit: if the newly picked
    // batch pushes the running total over the cap, reject the ENTIRE new
    // batch rather than silently trimming it to fit.
    const existingSize = this.attachments.reduce((sum, a) => sum + a.file.size, 0);
    const pickedSize = picked.reduce((sum, f) => sum + f.size, 0);
    if (existingSize + pickedSize > this.maxTotalSizeBytes) {
      const maxMb = Math.round(this.maxTotalSizeBytes / (1024 * 1024));
      safeToast(`Total attachment size can't exceed ${maxMb}MB.`, 'error');
      return;
    }

    for (const file of picked) {
      const isImage = file.type.startsWith('image/') || /\.(png|jpe?g)$/i.test(file.name);
      this.attachments.push({
        file,
        isImage,
        objectUrl: isImage ? URL.createObjectURL(file) : null,
      });
    }
  }

  removeAttachment(index: number): void {
    const [removed] = this.attachments.splice(index, 1);
    if (removed?.objectUrl) { URL.revokeObjectURL(removed.objectUrl); }
  }

  get attachmentCountLabel(): string {
    return `${this.attachments.length}/${this.maxAttachmentCount}`;
  }

  // Browser-appropriate stand-ins for the mobile app's deviceModel/osVersion
  // FormData fields — there's no real device model/OS on the web. The
  // backend expects a short, valid-looking device name (the raw
  // navigator.userAgent string is rejected as invalid), so parse a short
  // "Browser on OS" label from it instead.
  private get deviceModel(): string {
    const ua = navigator.userAgent || '';
    const browser =
      /Edg\//.test(ua) ? 'Edge' :
      /OPR\//.test(ua) ? 'Opera' :
      /Chrome\//.test(ua) ? 'Chrome' :
      /Firefox\//.test(ua) ? 'Firefox' :
      /Safari\//.test(ua) ? 'Safari' : 'Browser';
    const os =
      /Windows/.test(ua) ? 'Windows' :
      /Mac OS X/.test(ua) ? 'macOS' :
      /Android/.test(ua) ? 'Android' :
      /iPhone|iPad|iPod/.test(ua) ? 'iOS' :
      /Linux/.test(ua) ? 'Linux' : 'Web';
    return `${browser} on ${os}`;
  }

  private get osVersion(): string {
    const ua = navigator.userAgent || '';
    const macMatch = ua.match(/Mac OS X (\d+[_.]\d+(?:[_.]\d+)?)/);
    if (macMatch) { return `macOS ${macMatch[1].replace(/_/g, '.')}`; }
    const winMatch = ua.match(/Windows NT (\d+\.\d+)/);
    if (winMatch) { return `Windows NT ${winMatch[1]}`; }
    const androidMatch = ua.match(/Android (\d+(?:\.\d+)?)/);
    if (androidMatch) { return `Android ${androidMatch[1]}`; }
    const iosMatch = ua.match(/OS (\d+[_.]\d+(?:[_.]\d+)?) like Mac OS X/);
    if (iosMatch) { return `iOS ${iosMatch[1].replace(/_/g, '.')}`; }
    return navigator.platform || 'Web';
  }

  // This app's login only ever asks for ONE of mobile-or-email (never
  // both — see parent-login.component.ts's loginMode), so only the field
  // actually used to log in is sent; the other is left blank rather than
  // forcing a value the parent never provided.
  private get loggedInMobileOrEmail(): string {
    try {
      return JSON.parse(localStorage.getItem('mobileNumber') || '""');
    } catch {
      return localStorage.getItem('mobileNumber') || '';
    }
  }

  // First phone-like value found on the stored student/parent details (any key containing "mobile").
  private registeredMobileFromSession(): string {
    try {
      const list = JSON.parse(localStorage.getItem('studentDetails') || '[]');
      for (const item of Array.isArray(list) ? list : []) {
        for (const key of Object.keys(item || {})) {
          const val = item[key];
          if (/mobile/i.test(key) && (typeof val === 'string' || typeof val === 'number')) {
            const digits = String(val).replace(/\D/g, '');
            if (digits.length >= 6) { return digits; }
          }
        }
      }
    } catch { /* fall through */ }
    return '';
  }

  private get loginMode(): string {
    return localStorage.getItem('userLoginedMode') || '';
  }

  private get countryDialCode(): string {
    // No dedicated storage key for this exists post-login; the value used
    // during login itself isn't persisted separately from mobileNumber, so
    // this mirrors parent-login.component.ts's own default fallback.
    return localStorage.getItem('countryDialCode') || '91';
  }

  submitFeedback(): void {
    this.submitError = '';
    const trimmed = this.message.trim();
    if (!trimmed) {
      const msg = 'Please enter your feedback before submitting.';
      this.submitError = msg;
      safeToast(msg, 'error');
      return;
    }

    const formData = new FormData();
    formData.append('message', trimmed);
    formData.append('deviceModel', this.deviceModel);
    formData.append('osVersion', this.osVersion);

    const contactValue = this.loggedInMobileOrEmail;
    if (this.loginMode === 'email') {
      // The feedback API rejects a blank mobileNumber ("Please provide valid mobile number",
      // 422), so email-login parents send their registered mobile when the session carries
      // one, and otherwise the email in the same field (the convention the other
      // email-login APIs here use).
      const registeredMobile = this.registeredMobileFromSession();
      formData.append('emailId', contactValue);
      formData.append('mobileNumber', registeredMobile || contactValue);
      formData.append('countryCode', registeredMobile ? this.countryDialCode : '');
    } else {
      formData.append('mobileNumber', contactValue);
      formData.append('countryCode', this.countryDialCode);
      formData.append('emailId', '');
    }

    this.attachments.forEach(a => formData.append('file', a.file));

    this.submitting = true;
    this.feedbackService.submitFeedback(formData).subscribe({
      next: (res: any) => {
        this.submitting = false;
        if (res && res.status) {
          this.showSubmitModal = false;
          this.revokeAttachmentUrls();
          this.attachments = [];
          safeToast('Feedback submitted successfully.', 'success');
          this.currentPage = 1;
          this.loadList();
        } else {
          const msg = (res && res.message) || 'Unable to submit feedback. Please try again.';
          this.submitError = msg;
          safeToast(msg, 'error');
        }
      },
      error: (err) => {
        this.submitting = false;
        const msg = (err && err.error && err.error.message) || 'Unable to submit feedback. Please try again.';
        this.submitError = msg;
        safeToast(msg, 'error');
      },
    });
  }
}
